import React from 'react';
import PropTypes from 'prop-types';
import { getStepRedirectPage, getStepDataTrack } from '../constants';
import {
  DesktopStepperContainer,
  MobileStepperWrapper,
  StepperWrapper,
  StepConnector,
  StepNode,
  StepCircle,
  StepLabel,
  BreadcrumbChevron,
} from './Stepper.styles';


const Stepper = ({ steps, currentIndex, pageName, navigate }) => {
  const canNavigateToStep = (stepIndex) => stepIndex < currentIndex;

  const handleStepClick = (stepIndex) => {
    if (!canNavigateToStep(stepIndex)) return;
    const page = getStepRedirectPage(steps[stepIndex]?.id);
    if (page) {
      const separator = page.includes('?') ? '&' : '?';
      navigate(`/sales/home/${page}${separator}from=progressbar_${pageName}`);
    }
  };

  const handleStepKeyDown = (e, stepIndex) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleStepClick(stepIndex);
    }
  };

  const renderStepNode = (step, idx, isCompleted, isActive, isClickable) => {
    const interactiveProps = isClickable
      ? {
          onClick: () => handleStepClick(idx),
          onKeyDown: (e) => handleStepKeyDown(e, idx),
          tabIndex: 0,
        }
      : { tabIndex: -1 };

    return (
      <StepNode isClickable={isClickable}>
        <StepCircle
          className="progress-step-circle"
          as={isClickable ? 'button' : 'div'}
          type={isClickable ? 'button' : undefined}
          isCompleted={isCompleted}
          isActive={isActive}
          isClickable={isClickable}
          aria-label={step.label}
          aria-current={isActive ? 'step' : undefined}
          data-track={getStepDataTrack(step.id)}
          data-testid={`progress-step-${step.id}`}
          {...interactiveProps}
        >
          {idx + 1}
        </StepCircle>
        <StepLabel
          className="progress-step-label"
          isActive={isActive}
          isCompleted={isCompleted}
          role={isClickable ? 'button' : undefined}
          aria-pressed={isActive || isClickable}
          data-track={getStepDataTrack(step.id)}
          data-testid={`progress-label-${step.id}`}
          {...interactiveProps}
        >
          {step.label}
        </StepLabel>
      </StepNode>
    );
  };

  const stepContent = steps.map((step, idx) => {
    const isCompleted = idx < currentIndex;
    const isActive = idx === currentIndex;
    const isClickable = canNavigateToStep(idx);
    const isNotLast = idx < steps.length - 1;

    return (
      <React.Fragment key={step.id}>
        {renderStepNode(step, idx, isCompleted, isActive, isClickable)}

        {/* Desktop: horizontal connector line between steps */}
        {isNotLast && <StepConnector aria-hidden="true" />}

        {/* Mobile: thin chevron separator between breadcrumb labels */}
        {isNotLast && (
          <BreadcrumbChevron aria-hidden="true">&rsaquo;</BreadcrumbChevron>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      {/* Desktop stepper inside an asymmetrically-padded container */}
      <DesktopStepperContainer>
        <StepperWrapper aria-label="Order progress" role="navigation">
          {stepContent}
        </StepperWrapper>
      </DesktopStepperContainer>

      {/* Mobile breadcrumb stepper */}
      <MobileStepperWrapper aria-label="Order progress" role="navigation">
        {stepContent}
      </MobileStepperWrapper>
    </>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      pages: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  currentIndex: PropTypes.number.isRequired,
  pageName: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default Stepper;
